import { Fragment, useState, useEffect, useMemo } from 'react';
import type { Transcript } from './SearchTranscript.types';
import { knowledgeBaseService } from '../services/knowledgeBaseService';

interface ExtractionSummaryViewProps {
  result: Record<string, unknown>;
  transcript: Transcript;
  kbName: string | null;
}

const SCORE_THRESHOLD_BANNER = 60;

export function ExtractionSummaryView({ result, transcript, kbName }: ExtractionSummaryViewProps) {
  const [docSortBy, setDocSortBy] = useState<'score' | 'title'>('score');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [targetKbId, setTargetKbId] = useState('');
  const [kbList, setKbList] = useState<Array<{ id: string; name: string }>>([]);
  const [addStatus, setAddStatus] = useState<null | 'loading' | 'success' | 'error'>(null);
  const [addMessage, setAddMessage] = useState('');
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const regime         = (result['regime'] as string) ?? '?';
  const kbDisplayName  = kbName ?? (result['kb_id'] as string) ?? '—';
  const intent         = (result['intent'] as string) ?? '';
  const targetContext  = (result['target_context'] as string) ?? '';
  const totalDocs      = (result['total_documents_in_kb'] as number) ?? 0;
  const docsReturned   = (result['documents_returned'] as number) ?? 0;
  const pass2Count     = (result['pass2_count'] as number) ?? 0;
  const durationSec    = (result['total_duration_seconds'] as number) ?? 0;
  const dist           = ((result['score_distribution'] ?? {}) as Record<string, number>);
  const hits           = ((result['document_hits'] ?? []) as Array<Record<string, unknown>>);

  const formatDuration = (sec: number) => sec < 60 ? `${Math.round(sec)}s` : `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;

  const distBuckets = [
    { key: '0-20',   label: '0-20',   color: '#cbd5e1' },
    { key: '21-40',  label: '21-40',  color: '#fde68a' },
    { key: '41-60',  label: '41-60',  color: '#fdba74' },
    { key: '61-80',  label: '61-80',  color: '#86efac' },
    { key: '81-100', label: '81-100', color: '#4ade80' },
  ];
  const distTotal = distBuckets.reduce((s, b) => s + (dist[b.key] ?? 0), 0);

  const sortedHits = useMemo(() => {
    const arr = [...hits];
    if (docSortBy === 'score') arr.sort((a, b) => ((b['score'] as number) ?? 0) - ((a['score'] as number) ?? 0));
    else arr.sort((a, b) => String(a['title'] ?? '').localeCompare(String(b['title'] ?? '')));
    return arr;
  }, [hits, docSortBy]);

  const aboveThreshold = useMemo(() => hits.filter(h => ((h['score'] as number) ?? 0) >= SCORE_THRESHOLD_BANNER), [hits]);

  const scoreBadge = (score: number) => {
    if (score >= 61) return { bg: '#d1fae5', text: '#065f46' };
    if (score >= 41) return { bg: '#fef9c3', text: '#713f12' };
    return { bg: '#f3f4f6', text: '#4b5563' };
  };

  const TARGET_CONTEXT_LABELS: Record<string, string> = {
    state_of_the_art: 'State of the Art', fto: 'Freedom to Operate',
    patentability: 'Patentability', our_solution: 'Our Solution',
    industrialization: 'Industrialization', patent_application: 'Patent Application',
  };

  useEffect(() => {
    knowledgeBaseService.getAll().then(kbs => {
      setKbList(kbs.map(k => ({ id: String(k.id), name: k.name })));
      if (kbs.length > 0) setTargetKbId(String(kbs[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const ids = new Set(
      hits
        .filter(h => ((h['score'] as number) ?? 0) >= SCORE_THRESHOLD_BANNER)
        .map(h => String(h['doc_id'] ?? ''))
        .filter(Boolean)
    );
    setSelectedIds(ids);
  }, [hits]);

  const toggleSelect = (docId: string) => {
    if (!docId || docId.startsWith('__missing_')) return;
    setSelectedIds(prev => { const n = new Set(prev); if (n.has(docId)) n.delete(docId); else n.add(docId); return n; });
  };
  const selectAll  = () => setSelectedIds(new Set(hits.map(h => String(h['doc_id'] ?? '')).filter(Boolean)));
  const selectNone = () => setSelectedIds(new Set());
  const selectAboveThreshold = () => setSelectedIds(new Set(
    hits.filter(h => ((h['score'] as number) ?? 0) >= SCORE_THRESHOLD_BANNER).map(h => String(h['doc_id'] ?? '')).filter(Boolean)
  ));

  const handleAddToKb = async (docIds: string[], kbId: string) => {
    const validDocIds = docIds.filter(id => id && id.trim().length > 0);
    if (!kbId || validDocIds.length === 0) return;
    // shadow parameter with filtered list
    docIds = validDocIds;
    setAddStatus('loading');
    setAddMessage('');
    try {
      const res = await knowledgeBaseService.bulkAddDocuments(kbId, docIds);
      setAddStatus('success');
      const kbLabel = kbList.find(k => k.id === kbId)?.name ?? kbId;
      setAddMessage(`${res.added} document${res.added !== 1 ? 's' : ''} added to "${kbLabel}"${res.skipped > 0 ? ` (${res.skipped} already present)` : ''}.`);
    } catch {
      setAddStatus('error');
      setAddMessage('Failed to add documents. Please try again.');
    }
  };

  return (
    <div className="frs-summary">
      {/* Option C — Quick-save banner */}
      {!bannerDismissed && aboveThreshold.length > 0 && transcript.status === 'completed' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', padding: '0.6rem 0.9rem', marginBottom: '0.75rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: '0.82rem' }}>
          <span style={{ color: '#14532d', fontWeight: 500 }}>{aboveThreshold.length} document{aboveThreshold.length !== 1 ? 's' : ''} scored ≥ {SCORE_THRESHOLD_BANNER} — add to KB:</span>
          <select value={targetKbId} onChange={e => setTargetKbId(e.target.value)} style={{ fontSize: '0.8rem', padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db' }}>
            {kbList.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
          <button
            onClick={() => handleAddToKb(aboveThreshold.map(h => String(h['doc_id'] ?? '')).filter(Boolean), targetKbId)}
            disabled={addStatus === 'loading' || !targetKbId}
            style={{ fontSize: '0.8rem', padding: '2px 10px', borderRadius: 4, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer' }}
          >{addStatus === 'loading' ? 'Adding...' : `Add ${aboveThreshold.length}`}</button>
          {addStatus === 'success' && <span style={{ color: '#15803d' }}>{addMessage}</span>}
          {addStatus === 'error' && <span style={{ color: '#dc2626' }}>{addMessage}</span>}
          <button onClick={() => setBannerDismissed(true)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1rem', lineHeight: 1 }}>×</button>
        </div>
      )}

      <div className="frs-context-bar">
        <span className="frs-context-type">Document Retrieval {regime === 'A' ? '(Full Review)' : '(Hybrid Search)'}</span>
        <span className="frs-context-sep">|</span>
        <span className="frs-context-kb">{kbDisplayName}</span>
        {transcript.started_at && (
          <><span className="frs-context-sep">|</span>
          <span className="frs-context-date">{new Date(transcript.started_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span></>
        )}
        <span className="frs-context-sep">|</span>
        <span className="frs-context-duration">{formatDuration(durationSec)}</span>
      </div>

      <div className="frs-stats-row">
        <div className="frs-stat-card"><div className="frs-stat-value">{totalDocs.toLocaleString()}</div><div className="frs-stat-label">Docs in KB</div></div>
        <div className="frs-stat-card"><div className="frs-stat-value">{regime === 'A' ? 'Full Review' : 'Hybrid'}</div><div className="frs-stat-label">Strategy</div></div>
        <div className="frs-stat-card frs-stat-accent"><div className="frs-stat-value">{docsReturned.toLocaleString()}</div><div className="frs-stat-label">Documents scored</div></div>
        <div className="frs-stat-card"><div className="frs-stat-value">{pass2Count.toLocaleString()}</div><div className="frs-stat-label">With explanation</div></div>
        <div className="frs-stat-card"><div className="frs-stat-value">{TARGET_CONTEXT_LABELS[targetContext] ?? targetContext}</div><div className="frs-stat-label">Context</div></div>
      </div>

      {intent && (
        <div className="frs-section">
          <div className="frs-section-title">Search Intent</div>
          <div style={{ fontSize: '0.875rem', color: '#374151', fontStyle: 'italic', padding: '0.5rem 0.75rem', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
            "{intent}"
          </div>
        </div>
      )}

      {regime === 'A' && (
        <div className="frs-section">
          <div className="frs-section-title">Pipeline</div>
          <div style={{ fontSize: '0.78rem', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 4 }}>Pass 1 — Batch 1 (50%)</span>
            <span>{'→'}</span>
            <span style={{ background: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: 4 }}>Pass 2 top-25 B1 ∥ Pass 1 Batch 2 (50%)</span>
            <span>{'→'}</span>
            <span style={{ background: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: 4 }}>Pass 2 top-25 B2</span>
          </div>
        </div>
      )}

      {regime === 'B' && (
        <div className="frs-section">
          <div className="frs-section-title">Search Strategy</div>
          <div style={{ fontSize: '0.78rem', color: '#4b5563', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 4 }}>Vector Search</span>
            <span>+</span>
            <span style={{ background: '#dcfce7', color: '#14532d', padding: '2px 8px', borderRadius: 4 }}>BM25 Search</span>
            <span>{'→'}</span>
            <span style={{ background: '#fef9c3', color: '#713f12', padding: '2px 8px', borderRadius: 4 }}>RRF Fusion</span>
            <span>{'→'}</span>
            <span style={{ background: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: 4 }}>Pass 2 top-50</span>
          </div>
        </div>
      )}

      {distTotal > 0 && (
        <div className="frs-section">
          <div className="frs-section-title">Score Distribution</div>
          <div className="frs-dist-bar">
            {distBuckets.map(b => {
              const count = dist[b.key] ?? 0;
              const pct = distTotal > 0 ? (count / distTotal) * 100 : 0;
              if (count === 0) return null;
              return (<div key={b.key} className="frs-dist-segment" style={{ width: `${pct}%`, background: b.color }} title={`${b.label}: ${count} doc${count !== 1 ? 's' : ''}`} />);
            })}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
            {distBuckets.map(b => {
              const count = dist[b.key] ?? 0;
              if (count === 0) return null;
              return (<span key={b.key} style={{ fontSize: '0.7rem', color: '#6b7280' }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: b.color, marginRight: 3 }} />{b.label}: {count}</span>);
            })}
          </div>
        </div>
      )}

      {hits.length > 0 && (
        <div className="frs-section">
          {/* Option A — header row: sort + selection controls + add panel */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            <div className="frs-section-title" style={{ marginBottom: 0 }}>Documents ({hits.length})</div>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button onClick={() => setDocSortBy('score')} style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 3, border: '1px solid #e2e8f0', background: docSortBy === 'score' ? '#0f172a' : '#f8fafc', color: docSortBy === 'score' ? '#fff' : '#374151', cursor: 'pointer' }}>By Score</button>
              <button onClick={() => setDocSortBy('title')} style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 3, border: '1px solid #e2e8f0', background: docSortBy === 'title' ? '#0f172a' : '#f8fafc', color: docSortBy === 'title' ? '#fff' : '#374151', cursor: 'pointer' }}>By Title</button>
            </div>
            <div style={{ display: 'flex', gap: '0.25rem', marginLeft: '0.5rem' }}>
              <button onClick={selectAboveThreshold} style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 3, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', cursor: 'pointer' }}>≥{SCORE_THRESHOLD_BANNER}</button>
              <button onClick={selectAll}  style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 3, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', cursor: 'pointer' }}>All</button>
              <button onClick={selectNone} style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 3, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', cursor: 'pointer' }}>None</button>
            </div>
            {selectedIds.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: 'auto', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', color: '#374151' }}>{selectedIds.size} selected — add to:</span>
                <select value={targetKbId} onChange={e => setTargetKbId(e.target.value)} style={{ fontSize: '0.78rem', padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db' }}>
                  {kbList.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
                <button
                  onClick={() => handleAddToKb([...selectedIds], targetKbId)}
                  disabled={addStatus === 'loading' || !targetKbId}
                  style={{ fontSize: '0.78rem', padding: '2px 10px', borderRadius: 4, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'pointer' }}
                >{addStatus === 'loading' ? 'Adding...' : 'Add to KB'}</button>
                {addStatus === 'success' && <span style={{ fontSize: '0.75rem', color: '#15803d' }}>{addMessage}</span>}
                {addStatus === 'error'   && <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>{addMessage}</span>}
              </div>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: '0.78rem', borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '4px 6px 4px 0', width: 20 }}>
                    <input type="checkbox" checked={selectedIds.size === hits.length && hits.length > 0} onChange={e => e.target.checked ? selectAll() : selectNone()} style={{ cursor: 'pointer' }} />
                  </th>
                  <th style={{ padding: '4px 4px 4px 0', textAlign: 'center', color: '#6b7280', fontWeight: 'normal', width: 24 }}>#</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 'normal' }}>Title</th>
                  <th style={{ padding: '4px 8px', textAlign: 'center', color: '#6b7280', fontWeight: 'normal' }}>Score</th>
                  <th style={{ padding: '4px 8px', textAlign: 'center', color: '#6b7280', fontWeight: 'normal' }}>Method</th>
                  <th style={{ padding: '4px 0', textAlign: 'left', color: '#6b7280', fontWeight: 'normal' }}>Justification</th>
                </tr>
              </thead>
              <tbody>
                {sortedHits.map((hit, i) => {
                  const score       = (hit['score'] as number) ?? 0;
                  const badge       = scoreBadge(score);
                  const docId       = hit['doc_id'] ? String(hit['doc_id']) : `__missing_${i}`;
                  const isExpanded  = expandedRows.has(docId);
                  const explanation = String(hit['relevance_explanation'] ?? '');
                  const keywords    = (hit['matched_keywords'] as string[]) ?? [];
                  const isSelected  = selectedIds.has(docId);
                  return (
                    <Fragment key={docId}>
                      <tr style={{ borderBottom: '1px solid #f1f5f9', background: isSelected ? '#f0f9ff' : undefined }}>
                        <td style={{ padding: '3px 6px 3px 0' }} onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(docId)} style={{ cursor: 'pointer' }} />
                        </td>
                        <td style={{ padding: '3px 4px 3px 0', textAlign: 'center', color: '#9ca3af' }}>{i + 1}</td>
                        <td style={{ padding: '3px 8px', fontWeight: 500, cursor: explanation ? 'pointer' : 'default' }}
                            onClick={() => explanation && setExpandedRows(prev => { const n = new Set(prev); if (n.has(docId)) n.delete(docId); else n.add(docId); return n; })}>
                          {String(hit['title'] ?? '')}
                          {keywords.length > 0 && <span style={{ marginLeft: 4, fontSize: '0.68rem', color: '#7c3aed' }}>{keywords.join(', ')}</span>}
                        </td>
                        <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                          <span style={{ background: badge.bg, color: badge.text, padding: '1px 6px', borderRadius: 3, fontSize: '0.75rem' }}>{score}</span>
                        </td>
                        <td style={{ padding: '3px 8px', textAlign: 'center', color: '#6b7280' }}>{String(hit['approach'] ?? '')}</td>
                        <td style={{ padding: '3px 0', color: '#6b7280' }}>{String(hit['justification'] ?? '')}</td>
                      </tr>
                      {isExpanded && explanation && (
                        <tr style={{ background: '#f8fafc' }}>
                          <td /><td />
                          <td colSpan={4} style={{ padding: '6px 8px 8px', fontSize: '0.76rem', color: '#374151', fontStyle: 'italic', borderBottom: '1px solid #e2e8f0' }}>
                            <strong>Relevance explanation:</strong> {explanation}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {hits.some(h => String(h['relevance_explanation'] ?? '')) && (
            <p style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.4rem' }}>Click a title to expand the Pass 2 relevance explanation.</p>
          )}
        </div>
      )}
    </div>
  );
}
